/**
 * Hydrates the Community flywheel card. Topic Trending and New rails live in
 * homeDiscoveryHydrator so each follows the Browse ranking endpoints.
 *
 * Community shows current seven-day proposals only. Desktop keeps its compact list; the guest phone surface
 * renders the lead request once as a compact voting decision card.
 */
import { byId, awaitMinSkeletonDelay } from "./domUtils";
import { formatCompact } from "./formatUtils";
import type { Root } from "./prototypeTypes";
import type { PrototypeApiClient } from "./apiClient";
import { displayStatus } from './communityIdeaPresentation';
import { renderEmptyStateMarkup } from './emptyStateMarkup';

let flywheelHydrationGeneration = 0;

/** Invalidates an in-flight visitor flywheel when Home is no longer visible. */
export function deactivateHomeFlywheel(): void {
	flywheelHydrationGeneration += 1;
}

export async function hydrateHomeFlywheel(
  root: Root,
  api: PrototypeApiClient,
): Promise<void> {
	const generation = ++flywheelHydrationGeneration;
	await hydrateFlywheelCard(root, api, () => generation === flywheelHydrationGeneration);
}

interface CommunityIdea {
  id: string;
  title: string;
  description: string | null;
  status: string;
  isAdopted?: boolean;
  navigableTopicSlug?: string | null;
  commentsCount: number;
  linksCount: number;
  attachmentsCount: number;
  category: string | null;
}

async function hydrateFlywheelCard(root: Root, api: PrototypeApiClient, isCurrent: () => boolean): Promise<void> {
  const desktopCard = byId(root, 'flywheelCard');
  const mobileCard = byId(root, 'flywheelMobileCard');
  if (!desktopCard && !mobileCard) return;
  try {
	const resp = (await api.listCommunityIdeas('recent', { limit: 3 })) as unknown as { ideas?: CommunityIdea[] };
		if (!isCurrent()) return;
    const ideas: CommunityIdea[] = resp.ideas ?? [];
    await awaitMinSkeletonDelay();
		if (!isCurrent()) return;
    if (ideas.length === 0) {
      const empty = () => buildEmpty('No active community requests right now', 'Check back soon for new ideas from the community.', 'message');
      desktopCard?.replaceChildren(empty());
      mobileCard?.replaceChildren(empty());
      return;
    }
    // Wrap cards + "View all" link in a flex column so they share the
    // panel height equally with the left-side flywheel-steps.
    const wrapper = document.createElement("div");
    wrapper.className = "flywheel-card-list";
    wrapper.replaceChildren(...ideas.slice(0, 2).map(buildFlywheelCard));

    const viewAll = document.createElement("a");
    viewAll.className = "flywheel-view-all";
		viewAll.tabIndex = 0;
		viewAll.setAttribute('role', 'link');
		viewAll.setAttribute('aria-label', 'View all community ideas');
		viewAll.dataset.keyboardAction = 'true';
    viewAll.setAttribute("data-proto-click", "showView('community');setActiveTab('community')");
    viewAll.textContent = "View all community ideas ->";
    wrapper.appendChild(viewAll);

    desktopCard?.replaceChildren(wrapper);
    mobileCard?.replaceChildren(buildGuestCommunityRequest(ideas[0], ideas.slice(0, 2)));
  } catch {
		if (!isCurrent()) return;
    const empty = () => buildEmpty("Couldn't load community requests", 'Check your connection and try again.', 'inbox');
    desktopCard?.replaceChildren(empty());
    mobileCard?.replaceChildren(empty());
  }
}

/** Compact SVG icons for the meta row, mirroring the community list footer. */
const META_ICON = {
  link: '<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>',
  attach: '<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>',
};

function buildFlywheelCard(idea: CommunityIdea): HTMLElement {
  const card = document.createElement("div");
  card.className = "flywheel-card flywheel-card--compact";
  card.tabIndex = 0;
  card.setAttribute('role', 'link');
  card.setAttribute('aria-label', `Open community request: ${idea.title}`);
  card.dataset.keyboardAction = 'true';
  card.setAttribute("data-proto-click", `showIdeaDetail('${idea.id}')`);

  // Line 1: title (single line, truncated)
  const title = document.createElement("h4");
  title.textContent = idea.title;
  card.appendChild(title);

  // Line 2: status pill + source metadata. The pill uses the same public
  // Voting/Selected vocabulary as the community list (communityIdeaPresentation):
  // raw workflow states like "In Progress" never reach the UI.
  const meta = document.createElement("div");
  meta.className = "flywheel-card-meta";

  const status = displayStatus(idea);
  const statusPill = document.createElement("span");
  statusPill.className = `pill ${status === 'Selected' ? 'pill-completed' : 'pill-adopted'}`;
  statusPill.textContent = status;
  meta.appendChild(statusPill);

  meta.appendChild(buildMetaItem(META_ICON.attach, formatCompact(idea.attachmentsCount)));
  meta.appendChild(buildMetaItem(META_ICON.link, formatCompact(idea.linksCount)));

  card.appendChild(meta);
  return card;
}

function buildMetaItem(iconHtml: string, value: string): HTMLElement {
  const item = document.createElement("span");
  item.className = "flywheel-meta-item";
  item.innerHTML = `${iconHtml}<span>${value}</span>`;
  return item;
}

const MACHINE_LIKE_COMMUNITY_TITLE = /^[a-z0-9]+(?:[-_][a-z0-9]+){2,}$/i;
const GUEST_COMMUNITY_STAGES = ['Requested', 'Supported', 'In production', 'Published'] as const;

/** Keeps generated operational slugs out of the compact guest Home card. */
export function resolveGuestCommunityDisplayTitle(idea: { title: string; description: string | null }): string {
	const title = idea.title.trim();
	const description = idea.description?.replace(/\s+/g, ' ').trim() ?? '';
	const isMachineLike = MACHINE_LIKE_COMMUNITY_TITLE.test(title) && /(?:^|[-_])(?:ops|community|s\d+|\d{6,})(?:[-_]|$)/i.test(title);

	if (isMachineLike) return description ? (description.length > 96 ? `${description.slice(0, 93).trimEnd()}…` : description) : 'A topic requested by the community';
	return title || description || 'Community request';
}

/**
 * The stage bar mirrors where the community's lead requests actually sit,
 * derived from the same public signals the community list uses (isAdopted +
 * navigableTopicSlug; raw workflow states never reach the UI): a pick whose
 * topic is already live completes the loop, an adopted pick is in production,
 * and otherwise the community is still collecting support.
 */
function guestCommunityStageIndex(pipeline: CommunityIdea[]): number {
	if (pipeline.some((idea) => idea.isAdopted && idea.navigableTopicSlug?.trim())) return 3;
	if (pipeline.some((idea) => idea.isAdopted)) return 2;
	return 1;
}

function buildGuestCommunityRequest(idea: CommunityIdea, pipeline: CommunityIdea[]): HTMLElement {
	const stageIndex = guestCommunityStageIndex(pipeline);
	const card = document.createElement('div');
	card.className = 'home-guest-community-request';

  const head = document.createElement('div');
  head.className = 'home-guest-community-request-head';
  const eyebrow = document.createElement('p');
  eyebrow.className = 'home-guest-community-request-eyebrow';
  eyebrow.textContent = 'Community pick';
  const title = document.createElement('h4');
  title.textContent = resolveGuestCommunityDisplayTitle(idea);
  const meta = document.createElement('p');
  meta.className = 'home-guest-community-request-meta';
  head.appendChild(eyebrow);
  head.appendChild(title);
  head.appendChild(meta);

  const stages = document.createElement('div');
  stages.className = 'home-guest-community-stages';
  stages.style.setProperty('--community-progress', `${stageIndex * 25}%`);
  stages.setAttribute('aria-label', `Request status: ${GUEST_COMMUNITY_STAGES[stageIndex]}`);
  GUEST_COMMUNITY_STAGES.forEach((label, index) => {
    const stage = document.createElement('span');
    stage.className = 'home-guest-community-stage';
    if (index < stageIndex) stage.classList.add('is-complete');
    if (index === stageIndex) {
      stage.classList.add('is-active');
      stage.setAttribute('aria-current', 'step');
      if (stageIndex === 2) stage.classList.add('is-processing');
    }
    const dot = document.createElement('span');
    dot.className = 'home-guest-community-stage-dot';
    dot.setAttribute('aria-hidden', 'true');
    const name = document.createElement('span');
    name.textContent = label;
    stage.appendChild(dot);
    stage.appendChild(name);
    stages.appendChild(stage);
  });

  const note = document.createElement('p');
  note.className = 'home-guest-community-request-note';
  note.textContent = 'Support what DeepMox builds next.';
  card.appendChild(head);
  card.appendChild(stages);
  card.appendChild(note);
  return card;
}

function buildEmpty(
	title: string,
	subtitle: string,
	icon: 'book' | 'inbox' | 'message' | 'sparkles',
): HTMLElement {
	const template = document.createElement('template');
	template.innerHTML = renderEmptyStateMarkup({ icon, title, subtitle, extraClass: 'home-topic-empty-state' });
	return template.content.firstElementChild as HTMLElement;
}
